import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getCallRecordings } from "../../api/transcriptions";
import {
  Container,
  Header,
  Icon,
  Segment,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";
import CallRecordingRow from "./CallRecordingRow";
import {
  RecordingsError,
  RecordingsLoading,
  RecordingsNotice,
  useRecordingsQuery,
} from "./CallRecordingsStatus";
import { style } from "../../screens/Sidekick";

const CallRecordings = ({
  setInfoModal,
  children,
  searchParams,
  setIsSearching,
  onViewProcessed,
}) => {
  const [sidekickHelpTextHiden, setSidekickHelpTextHidden] = useState(
    localStorage.getItem("sidekickHelpTextHidden") || false,
  );

  const queryClient = useQueryClient();

  const queryKey = ["call-recordings", searchParams];
  const status = useRecordingsQuery(
    queryKey,
    () => {
      return getCallRecordings(searchParams);
    },
    {
      keepPreviousData: true,
      staleTime: 60000,
      refetchIntervalInBackground: true,
      refetchInterval: (data) => {
        let interval = false;
        data?.data?.data?.forEach((i) => {
          if (
            ["SUMMARISING", "TRANSCRIBING"].includes(
              i?.transcription?.status.value,
            )
          ) {
            queryClient.invalidateQueries({
              queryKey: ["call-transcriptions"],
            });
            interval = 30 * 1000;
          }
        });
        return interval;
      },
    },
  );

  const recordingsQuery = status.query;

  useEffect(() => {
    setIsSearching(recordingsQuery.isRefetching);
  }, [setIsSearching, recordingsQuery.isRefetching]);

  if (status.mode === "loading") {
    return (
      <Container style={{ marginTop: "20px" }}>
        <RecordingsLoading
          slow={status.slow}
          ipecsIssue={status.ipecsIssue}
          attempt={status.attempt}
          maxAttempts={status.maxAttempts}
          columns={5}
        />
      </Container>
    );
  }

  if (status.mode === "error") {
    return (
      <Container style={{ marginTop: "20px" }}>
        <RecordingsError
          error={recordingsQuery.error}
          retrying={status.retrying}
          retryIn={status.retryIn}
          onRetry={status.retry}
          onViewProcessed={onViewProcessed}
        />
      </Container>
    );
  }

  const recordings = status.rows?.data?.data || [];

  return (
    <Container style={{ marginTop: "20px" }}>
      {!sidekickHelpTextHiden && (
        <Segment
          inverted
          style={{
            ...style.purpleGradientButton,
            marginBottom: "35px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <Header size="medium" style={{ width: "80%", marginBottom: "0px" }}>
              Sidekick turbobcharges your workflow by summarising calls,
              generating meeting notes and helping you identify next actions for
              your projects.
            </Header>
            <Icon
              name="x"
              size="large"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setSidekickHelpTextHidden(true);
                localStorage.setItem("sidekickHelpTextHidden", true);
              }}
            />
          </div>
          <p>
            <i>
              If you choose "transcribe only" on a call recording below you can
              still use AI on this call later at a later date.
            </i>
          </p>
        </Segment>
      )}
      {status.notice && (
        <RecordingsNotice
          kind={status.notice}
          ipecsIssue={status.ipecsIssue}
          updatedAt={status.updatedAt}
          attempt={status.attempt}
          maxAttempts={status.maxAttempts}
          retrying={status.retrying}
          retryIn={status.retryIn}
          onRetry={status.retry}
        />
      )}
      <Table basic="very" inverted>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Number</TableHeaderCell>
            <TableHeaderCell>Start Time</TableHeaderCell>
            <TableHeaderCell>End Time</TableHeaderCell>
            <TableHeaderCell>Duration</TableHeaderCell>
            <TableHeaderCell style={{ width: "140px" }}></TableHeaderCell>
          </TableRow>
        </TableHeader>

        <TableBody>
          {recordings.length === 0 && (
            <tr>
              <td colSpan={5} style={{ paddingTop: "12px" }}>
                No call recordings found.
              </td>
            </tr>
          )}

          {recordings.map((i) => {
            return (
              <CallRecordingRow
                id={i.slug}
                key={`recording-row-${i.slug}`}
                number={i.tel_number}
                start={i.start}
                end={i.end}
                duration={i.duration}
                transcription={i.transcription}
                setInfoModal={setInfoModal}
                searchParams={searchParams}
                actionsDisabledReason={status.actionsDisabledReason}
              />
            );
          })}
        </TableBody>
      </Table>
      {children}
    </Container>
  );
};

export default CallRecordings;
